import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import InvoiceTable from '../../../components/tables/InvoiceTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useInvoiceApi } from '../../../hooks/useInvoiceApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete, showAlert } from '../../../utils/sweetAlertUtils';
import {
  ComponentDTO,
  InvoiceDTO,
  InvoiceTypeDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useInvoiceTypeApi } from '../../../hooks/useInvoiceTypeApi';
import { useComponents } from '../../../hooks/useComponentApi';
import { FileViewer } from '../../../components/common/FileViewer';
import GeneralSearch from '../../../components/common/GeneralSearch';
import { useSearchState } from '../../../utils/searchStateUtils/useSearchState';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';

const InvoiceList: React.FC = () => {
  const { search, remove, extract, getInvoiceSasUrl, resend, isLoading, error } = useInvoiceApi();
  const [items, setItems] = useState<InvoiceDTO[]>([]);
  
  // Search state persistence
  const {
    currentPage,
    pageSize,
    sortBy,
    sortDescending,
    searchFilters,
    setCurrentPage,
    setPageSize,
    setSortBy,
    setSortDescending,
    setSort,
    setSearchFilters,
    showAdvanced,
    setShowAdvanced,
    clearSearchState,
  } = useSearchState('invoices', {
    currentPage: 1,
    pageSize: 10,
    sortBy: 'CreatedUtc',
    sortDescending: true,
    searchFilters: {},
    showAdvanced: false,
  });
  
  const [totalRecords, setTotalRecords] = useState(0);

  // Local search term state (only commit to searchFilters on form submit)
  const [localSearchTerm, setLocalSearchTerm] = useState(searchFilters.generalSearchTerm || '');

  // File viewer state
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerFileName, setViewerFileName] = useState('');

  const navigate = useNavigate();

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Invoices', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: sortBy,
        sortDescending: sortDescending,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Invoices:', error);
      return 0;
    }
  };

  const handleSort = (sortKey: string) => {
    if (sortBy === sortKey) {
      setSort(sortKey, !sortDescending);
    } else {
      setSort(sortKey, true);
    }
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, sortBy, sortDescending]);

  const [invoiceTypes, setInvoiceTypes] = useState<InvoiceTypeDTO[]>([]);
  const { search: searchInvoiceTypes, isLoading: isLoadingInvoiceTypes } = useInvoiceTypeApi();
  const fetchInvoiceTypes = async () => {
    try {
      const response = await searchInvoiceTypes({
        page: 1,
        pageSize: 9999,
        sortBy: '',
        sortDescending: false,
      });
      setInvoiceTypes(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching invoice types:', error);
    }
  };

  const [components, setComponents] = useState<ComponentDTO[]>([]);
  const { data: componentsData, isLoading: isLoadingComponents } = useComponents();

  useEffect(() => {
    fetchInvoiceTypes();
  }, []);

  useEffect(() => {
    setComponents(componentsData || []);
  }, [componentsData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const filters = { ...searchFilters, generalSearchTerm: localSearchTerm };
    setSearchFilters(filters);
    setCurrentPage(1);
    fetchItems(1, pageSize, filters);
  };

  const handleClearSearch = () => {
    setLocalSearchTerm('');
    clearSearchState();
  };

  const handleClearGeneralSearch = () => {
    // Clear local state, then GeneralSearch will submit the form
    setLocalSearchTerm('');
    setSearchFilters({});
  };

  const handleDelete = async (dto: InvoiceDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting Invoice:', error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Invoices:', error);
    }
  };

  const handleViewMember = (invoice: InvoiceDTO) => {
    if (invoice.memberId) {
      navigate(`/admin/members/${invoice.memberId}`);
    }
  };

  const handleLoginAsMember = async (invoice: InvoiceDTO) => {
    if (!invoice.memberId) return;

    try {
      // Open impersonation in new tab - ImpersonationRedirect will handle API call and redirect
      window.open(`/admin/impersonate/${invoice.memberId}`, '_blank');
    } catch (error) {
      console.error('Error opening impersonation tab', error);
    }
  };

  const handleViewDocument = async (invoice: InvoiceDTO) => {
    try {
      const response = await getInvoiceSasUrl(invoice.id ?? '');
      const fileName = invoice.invoiceNumber
        ? `Invoice_${invoice.invoiceNumber}.pdf`
        : `PaymentNotice_${invoice.salesOrderNumber}.pdf`;

      if (response && response.success && response.data) {
        setViewerUrl(response.data);
        setViewerFileName(fileName);
        setIsViewerOpen(true);
      }
    } catch (error) {
      console.error('Error loading invoice document:', error);
    }
  };

  const handleResend = async (invoice: InvoiceDTO) => {
    try {
      const response = await resend(invoice.id ?? '');
      if (response && response.success) {
        await showAlert({
          title: 'Success',
          text: response.message || `${invoice.invoiceNumber ? 'Invoice' : 'Payment notice'} resent successfully!`,
          icon: 'success',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      console.error('Error resending invoice or notice:', error);
      await showAlert({
        title: 'Error',
        text: 'Failed to resend. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/invoices"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Finance.Invoice.View"
    >
      <Card
        title="Manage Invoices"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button variant="secondary" onClick={() => setShowAdvanced(!showAdvanced)}>
              {showAdvanced ? 'Basic Search' : 'Advanced Search'}
            </Button>
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
            <AddNewButton onClick={() => navigate('/admin/invoices/create')} />
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {/* Advanced Search Fields */}
          {showAdvanced && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="firstName"
                label="Search by First Name"
                value={searchFilters.firstName || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, firstName: e.target.value })}
                placeholder="Search by First Name"
              />
              <FormField
                id="lastName"
                label="Search by Last Name"
                value={searchFilters.lastName || ''}
                onChange={(e) => setSearchFilters({ ...searchFilters, lastName: e.target.value })}
                placeholder="Search by Last Name"
              />
              <FormField
                id="invoiceTypeId"
                label="Search by Invoice Type"
                value={searchFilters.invoiceTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    invoiceTypeId: e.target.value,
                  })
                }
                placeholder="Select Invoice Type"
                options={invoiceTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingInvoiceTypes}
              />
              <FormField
                id="adminComponentId"
                label="Search by Component"
                value={searchFilters.adminComponentId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    adminComponentId: e.target.value,
                  })
                }
                placeholder="Select Component"
                options={components.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoadingComponents}
              />
              <FormField
                id="invoiceNumber"
                label="Search by Invoice Number"
                value={searchFilters.invoiceNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    invoiceNumber: e.target.value,
                  })
                }
                placeholder="Search by Invoice Number"
              />
              <FormField
                id="salesOrderNumber"
                label="Search by Sales Order Number"
                value={searchFilters.salesOrderNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    salesOrderNumber: e.target.value,
                  })
                }
                placeholder="Search by Sales Order Number"
              />
              <div className="flex space-x-4 mb-4">
                <Button variant="secondary" onClick={handleClearSearch}>
                  Clear Search
                </Button>
                <Button type="submit">Search</Button>
              </div>
            </div>
          )}
          {!showAdvanced && (
            <GeneralSearch  
              searchFilters={{ generalSearchTerm: localSearchTerm }} 
              setSearchFilters={(filters) => setLocalSearchTerm(filters.generalSearchTerm || '')}
              onClear={handleClearGeneralSearch}
            />
          )}
        </form>

        <InvoiceTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/invoices/${dto.id}`)}
          handleDelete={handleDelete}
          handleViewMember={handleViewMember}
          handleLoginAsMember={handleLoginAsMember}
          handleViewDocument={handleViewDocument}
          handleResend={handleResend}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
          sortBy={sortBy}
          sortDescending={sortDescending}
          onSort={handleSort}
        />
      </Card>

      {isViewerOpen && (
        <FileViewer
          fileName={viewerFileName}
          sasUrl={viewerUrl}
          onClose={() => setIsViewerOpen(false)}
        />
      )}
    </AdminLayout>
  );
};

export default InvoiceList;
